/**
 * Company registration number validation, per country.
 * Czech IČO carries a mod11 checksum; Slovak IČO is digits only.
 */
import { CountryCode } from '@insio/countries';

export interface ValidationResult {
  valid: boolean;
  error?: string;
  /** Which check failed — lets callers pick their own message. */
  code?: 'format' | 'checksum';
}

const ICO_WEIGHTS = [8, 7, 6, 5, 4, 3, 2];

/**
 * Validate Czech IČO format and checksum.
 */

export function validateIco(ico: string): ValidationResult {
  const trimmed = ico.trim();

  if (!/^\d{1,8}$/.test(trimmed)) {
    return { valid: false, error: 'IČO must contain only digits (max 8)' };
  }

  const padded = trimmed.padStart(8, '0');

  let sum = 0;
  for (let i = 0; i < 7; i++) {
    sum += parseInt(padded[i], 10) * ICO_WEIGHTS[i];
  }

  const expectedCheckDigit = (11 - (sum % 11)) % 10;
  const actualCheckDigit = parseInt(padded[7], 10);

  if (actualCheckDigit !== expectedCheckDigit) {
    return { valid: false, error: 'IČO checksum is invalid' };
  }

  return { valid: true };
}

export function isValidIco(ico: string): boolean {
  return validateIco(ico).valid;
}

export interface CompanyIdRule {
  /** Shape of the bare company registration number. */
  readonly pattern: RegExp;
  /** Checksum verifier, or null when the country has none. */
  readonly checksum: ((value: string) => boolean) | null;
}

/**
 * Per-country company-ID rules, and the single list of countries whose business
 * register we can query — adding one here forces a matching registry client on the
 * backend. Slovakia dropped the mod11 checksum, so an 8-digit SK number can only be
 * confirmed by the register itself.
 */
export const COMPANY_ID_RULES = {
  [CountryCode.CZ]: { pattern: /^\d{8}$/, checksum: isValidIco },
  [CountryCode.SK]: { pattern: /^\d{8}$/, checksum: null }
} as const satisfies Partial<Record<CountryCode, CompanyIdRule>>;

export type CompanyRegistryCountry = keyof typeof COMPANY_ID_RULES;

/** Whether a company lookup is available for this country. */
export function hasCompanyRegistry(
  countryCode: string | null | undefined
): countryCode is CompanyRegistryCountry {
  return !!countryCode && countryCode in COMPANY_ID_RULES;
}

/**
 * Validate a bare company registration number against its country's rule.
 * `code` distinguishes a malformed value from one that cannot identify a company.
 */
export function validateCompanyId(
  companyId: string,
  countryCode: CompanyRegistryCountry
): ValidationResult {
  const rule = COMPANY_ID_RULES[countryCode];
  const trimmed = companyId.trim();

  if (!rule.pattern.test(trimmed)) {
    return {
      valid: false,
      code: 'format',
      error: `${countryCode} company ID has an invalid format`
    };
  }
  if (rule.checksum && !rule.checksum(trimmed)) {
    return {
      valid: false,
      code: 'checksum',
      error: `${countryCode} company ID checksum is invalid`
    };
  }

  return { valid: true };
}
